// src/pages/CreatorsPage.jsx
import { Link } from "react-router-dom";
import { FaTiktok, FaTwitch, FaYoutube, FaTwitter, FaInstagram } from "react-icons/fa";
import { usePlayersByGroup } from "../hooks/usePlayers";
import RosterSkeleton from "../components/RosterSkeleton";
import PlayerPhoto from "../components/PlayerPhoto";

// Pinned order: Lemo first, then Partners, then the rest
const PINNED_ORDER = ["Lemo", "JamaicanShifu", "ChrisWZ", "Potaatochips"];

function CreatorsPage() {
  // Content Creators plus anyone cross-listed here from another division.
  // Who's cross-listed is managed per player in the admin panel.
  const { players: creators, loading } = usePlayersByGroup("creators");

  const pinned = PINNED_ORDER.map((id) => creators.find((p) => p.id === id)).filter(Boolean);
  // Creators proper, minus the pinned ones already shown above
  const rest = creators.filter(
    (p) => p.division === "Content Creators" && !PINNED_ORDER.includes(p.id)
  );
  // Guests from other divisions go last
  const extras = creators.filter(
    (p) => p.division !== "Content Creators" && !PINNED_ORDER.includes(p.id)
  );
  const members = [...pinned, ...rest, ...extras];

  return (
    <main className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-red-950/40 to-black">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: `linear-gradient(rgba(220,38,38,0.3) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(220,38,38,0.3) 1px, transparent 1px)`,
              backgroundSize: "30px 30px",
            }}
          ></div>
        </div>
        <div className="absolute top-1/3 left-1/3 w-96 h-96 bg-red-600/20 blur-[128px] rounded-full pointer-events-none"></div>
        <div className="max-w-6xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl font-black uppercase tracking-tight text-white mb-6">
            <span className="text-red-600">Creators</span>
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl">
            The content creators and streamers of Prime Valor
          </p>
        </div>
      </section>

      {/* Members Grid */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          {loading && <RosterSkeleton count={8} />}

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {members.map((player) => (
              <div
                key={player.id}
                className="group bg-gray-900 border border-gray-800 rounded-lg overflow-hidden hover:border-red-600/50 transition-all duration-300 hover:shadow-lg hover:shadow-red-900/20"
              >
                {/* Image */}
                <Link to={`/roster/${player.id}`} className="block aspect-square overflow-hidden bg-gray-800">
                  <PlayerPhoto player={player} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </Link>

                {/* Info */}
                <div className="p-4">
                  <span className="text-red-500 text-xs font-semibold uppercase tracking-wider">
                    {player.title || "Content Creator"}
                  </span>
                  <Link to={`/roster/${player.id}`} className="block">
                    <h3 className="text-white font-bold text-lg mt-1 group-hover:text-red-400 transition-colors">
                      {player.name}
                    </h3>
                  </Link>
                  <p className="text-gray-500 text-sm mt-1">{player.country}</p>
                  <div className="flex gap-2 mt-2 flex-wrap">
                    {player.socials?.tiktok && (
                      <a href={player.socials.tiktok} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-red-500 transition-colors">
                        <FaTiktok size={14} />
                      </a>
                    )}
                    {player.socials?.twitch && (
                      <a href={player.socials.twitch} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-purple-500 transition-colors">
                        <FaTwitch size={14} />
                      </a>
                    )}
                    {player.socials?.youtube && (
                      <a href={player.socials.youtube} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-red-600 transition-colors">
                        <FaYoutube size={14} />
                      </a>
                    )}
                    {player.socials?.twitter && (
                      <a href={player.socials.twitter} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-blue-400 transition-colors">
                        <FaTwitter size={14} />
                      </a>
                    )}
                    {player.socials?.instagram && (
                      <a href={player.socials.instagram} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-pink-500 transition-colors">
                        <FaInstagram size={14} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}

export default CreatorsPage;
